import { z } from 'zod';
import { PCMP_CHANNELS, type PcmpChannel } from './pcmpProposal.types';
import { pcmpCmpReportSchema } from './pcmpReport.types';
import {
  pcmpPageMetaSchema,
  pcmpQueueItemSchema,
  pcmpQueuePersonSchema,
  pcmpQueueSearchShape,
  pcmpSkippedProposalSchema,
  type PcmpQueueFilters,
} from './pcmpQueue.types';

export const PCMP_MIS_TABS = [
  'PENDING',
  'NOT_VERIFIED',
  'EXPLAINED',
  'ACCOUNTS_STAGING',
  'MARKETING_STAGING',
  'COMPLETED',
  'ON_HOLD',
] as const;
export type PcmpMisTab = (typeof PCMP_MIS_TABS)[number];

/** The page's tabs: the queue tabs plus the batch list, in legacy order. */
export const PCMP_MIS_VIEWS = [
  'PENDING',
  'NOT_VERIFIED',
  'EXPLAINED',
  'ACCOUNTS_STAGING',
  'MARKETING_STAGING',
  'BATCHES',
  'COMPLETED',
  'ON_HOLD',
] as const;
export type PcmpMisView = (typeof PCMP_MIS_VIEWS)[number];

export const pcmpMisQueueItemSchema = pcmpQueueItemSchema.extend({
  paymentSource: z.enum(PCMP_CHANNELS),
});

export type PcmpMisQueueItem = z.infer<typeof pcmpMisQueueItemSchema>;

export const paginatedPcmpMisQueueSchema = z.object({
  data: z.array(pcmpMisQueueItemSchema),
  meta: pcmpPageMetaSchema,
});

export type PaginatedPcmpMisQueue = z.infer<typeof paginatedPcmpMisQueueSchema>;

export const pcmpMisQueueCountsSchema = z.object({
  pending: z.number(),
  notVerified: z.number(),
  explained: z.number(),
  accountsStaging: z.number(),
  /** The proposed total of the whole tab, for "select all matching". */
  accountsStagingAmount: z.number(),
  marketingStaging: z.number(),
  marketingStagingAmount: z.number(),
  batches: z.number(),
  completed: z.number(),
  onHold: z.number(),
});

export type PcmpMisQueueCounts = z.infer<typeof pcmpMisQueueCountsSchema>;

export interface PcmpMisQueueParams extends PcmpQueueFilters {
  tab: PcmpMisTab;
  page: number;
  limit: number;
}

export const pcmpMisQueueSearchSchema = z.object({
  tab: z.enum(PCMP_MIS_VIEWS).optional().catch(undefined),
  ...pcmpQueueSearchShape,
});

export type PcmpMisQueueSearch = z.infer<typeof pcmpMisQueueSearchSchema>;

// Routing batches (API LB-756).

export const PCMP_BATCH_STATUSES = ['STAGED', 'PUBLISHED', 'CLOSED'] as const;
export type PcmpBatchStatus = (typeof PCMP_BATCH_STATUSES)[number];

export const pcmpMisBatchSchema = z.object({
  batchId: z.coerce.number(),
  batchNo: z.number(),
  channel: z.enum(PCMP_CHANNELS).nullable(),
  status: z.enum(PCMP_BATCH_STATUSES),
  itemCount: z.number(),
  totalAmount: z.number(),
  /** Members matching the filters; null when none is set. */
  matchedItemCount: z.number().nullable(),
  stagedAt: z.string(),
  stagedBy: pcmpQueuePersonSchema.nullable(),
  publishedAt: z.string().nullable(),
  publishedBy: pcmpQueuePersonSchema.nullable(),
  /** MIS's remark at Publish; null while staged (LDS-803). */
  publishComment: z.string().nullish(),
});

export type PcmpMisBatch = z.infer<typeof pcmpMisBatchSchema>;

const batchTotalSchema = z.object({ batches: z.number(), count: z.number(), amount: z.number() });

export const pcmpMisBatchesSchema = z.object({
  totals: z.object({
    all: batchTotalSchema,
    accounts: batchTotalSchema,
    marketing: batchTotalSchema,
  }),
  batches: z.object({ items: z.array(pcmpMisBatchSchema), meta: pcmpPageMetaSchema }),
});

export type PcmpMisBatches = z.infer<typeof pcmpMisBatchesSchema>;

export const pcmpMisBatchItemSchema = pcmpMisQueueItemSchema.extend({
  canRollback: z.boolean(),
  rollbackBlockedReason: z.string().nullable(),
});

export type PcmpMisBatchItem = z.infer<typeof pcmpMisBatchItemSchema>;

export const pcmpMisBatchProposalsSchema = z.object({
  batch: pcmpMisBatchSchema,
  /** True when at least one item in the batch can be rolled back (LDS-823). */
  batchHasRollbackItems: z.boolean().optional().default(false),
  /** Filters narrow the members (and `showAll` is off). */
  isFiltered: z.boolean(),
  proposals: z.object({ items: z.array(pcmpMisBatchItemSchema), meta: pcmpPageMetaSchema }),
});

export type PcmpMisBatchProposals = z.infer<typeof pcmpMisBatchProposalsSchema>;

export interface PcmpMisBatchesParams extends PcmpQueueFilters {
  page: number;
  limit: number;
}

export interface PcmpMisBatchProposalsParams extends PcmpMisBatchesParams {
  showAll?: boolean | undefined;
}

export const pcmpMisBatchSearchSchema = z.object({
  showAll: z.boolean().optional().catch(undefined),
  ...pcmpQueueSearchShape,
});

export type PcmpMisBatchSearch = z.infer<typeof pcmpMisBatchSearchSchema>;

/** The picked ids, or everything the staging tab lists under the filters. */
export type PcmpMisStageSelection =
  | { proposalIds: number[] }
  | { selectAllMatching: true; filters: PcmpQueueFilters; excludedProposalIds: number[] };

export type PcmpMisStagePayload = { channel: PcmpChannel; comment: string } & PcmpMisStageSelection;

export const pcmpMisStageResultSchema = z.object({
  batchId: z.coerce.number(),
  batchNo: z.number(),
  channel: z.enum(PCMP_CHANNELS),
  staged: z.array(z.coerce.number()),
  skipped: z.array(pcmpSkippedProposalSchema),
});

export type PcmpMisStageResult = z.infer<typeof pcmpMisStageResultSchema>;

export const pcmpMisPublishResultSchema = z.object({
  batchNo: z.number(),
  channel: z.enum(PCMP_CHANNELS),
  published: z.array(z.coerce.number()),
  alreadyRouted: z.array(z.coerce.number()),
  paymentSourceSwitched: z.array(z.coerce.number()),
});

export type PcmpMisPublishResult = z.infer<typeof pcmpMisPublishResultSchema>;

export interface PcmpMisRollbackPayload {
  proposalIds: number[];
  targetPaymentSource: PcmpChannel;
  comment: string;
}

export const pcmpMisRollbackResultSchema = z.object({
  batchNo: z.number(),
  paymentSource: z.enum(PCMP_CHANNELS),
  rolledBack: z.array(z.coerce.number()),
  blocked: z.array(pcmpSkippedProposalSchema),
});

export type PcmpMisRollbackResult = z.infer<typeof pcmpMisRollbackResultSchema>;

// The CMP disbursement report of a routing batch.

export const pcmpMisBatchReportSchema = pcmpCmpReportSchema.extend({ batch: pcmpMisBatchSchema });

export type PcmpMisBatchReport = z.infer<typeof pcmpMisBatchReportSchema>;
